import axios from 'axios'
import { CSRFProtection, ClientRateLimit, SessionSecurity, SecureStorage } from '../utils/security'
import { validateUrl, validateApiEndpoint } from '../utils/https-validator'

// Determine API base URL with HTTPS enforcement
const getApiBaseUrl = () => {
  const envUrl = import.meta.env.VITE_API_URL
  
  if (!envUrl) {
    throw new Error("VITE_API_URL is not defined. Please set it in your .env file.")
  }
  
  // Use HTTPS validator to ensure secure URL
  const baseUrl = validateUrl(envUrl)
  
  return baseUrl
}

const API_BASE_URL = getApiBaseUrl()

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 second timeout
})

// Request interceptor to add auth token and security headers
api.interceptors.request.use(
  (config) => {
    // Check session integrity
    if (!SessionSecurity.validateSession()) {
      console.warn('Session integrity check failed')
      // Clear potentially compromised session
      SessionSecurity.clearSession()
      window.location.href = '/login'
      return Promise.reject(new Error('Session integrity check failed'))
    }

    // Rate limiting check
    const endpoint = config.url || ''
    if (!ClientRateLimit.isAllowed(endpoint)) {
      return Promise.reject(new Error('Rate limit exceeded. Please try again later.'))
    }

    // Add authentication token
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    // Add CSRF token for state-changing requests
    if (['post', 'put', 'patch', 'delete'].includes(config.method?.toLowerCase())) {
      const csrfToken = CSRFProtection.getToken()
      if (csrfToken) {
        config.headers['X-CSRF-Token'] = csrfToken
      }
    }

    // Add session ID if available
    const sessionId = SecureStorage.getSecureItem('session-id')
    if (sessionId) {
      config.headers['X-Session-ID'] = sessionId
    }

    // Add request timestamp for replay attack prevention
    config.headers['X-Request-Time'] = Date.now().toString()

    // Add client fingerprint
    const fingerprint = SessionSecurity.generateFingerprint()
    config.headers['X-Client-Fingerprint'] = fingerprint

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor to handle token refresh and security
api.interceptors.response.use(
  (response) => {
    // Extract and store CSRF token from response headers
    const csrfToken = response.headers['x-csrf-token']
    if (csrfToken) {
      CSRFProtection.setToken(csrfToken)
    }

    // Store session ID if provided
    const sessionId = response.headers['x-session-id']
    if (sessionId) {
      SecureStorage.setSecureItem('session-id', sessionId)
    }

    return response
  },
  async (error) => {
    const originalRequest = error.config

    // Handle different types of security errors
    if (error.response?.status === 429) {
      // Rate limit exceeded
      const retryAfter = error.response.headers['retry-after'] || 60
      console.warn(`Rate limit exceeded. Retry after ${retryAfter} seconds`)
      
      // Show user-friendly message
      throw new Error(`Too many requests. Please wait ${retryAfter} seconds before trying again.`)
    }

    if (error.response?.status === 403) {
      // Forbidden - could be CSRF token issue or insufficient permissions
      const errorDetail = error.response.data?.detail || ''
      
      if (errorDetail.includes('CSRF')) {
        console.warn('CSRF token validation failed')
        // Clear potentially invalid CSRF token
        CSRFProtection.setToken('')
        
        // Don't retry CSRF failures automatically to prevent loops
        throw new Error('Security validation failed. Please refresh the page.')
      }
      
      if (errorDetail.includes('Email verification required')) {
        // Redirect to email verification
        window.location.href = '/verify-email'
        return Promise.reject(error)
      }
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true

      try {
        const refreshToken = localStorage.getItem('refreshToken')
        
        if (refreshToken) {
          // Use a separate axios instance for refresh to avoid interceptor loops
          const refreshResponse = await axios.post(`${API_BASE_URL}/api/auth/refresh`, {
            refresh_token: refreshToken
          }, {
            timeout: 10000 // 10 second timeout for refresh
          })
          
          const { access_token } = refreshResponse.data
          localStorage.setItem('token', access_token)
          
          // Update the original request with new token
          originalRequest.headers.Authorization = `Bearer ${access_token}`
          
          // Retry original request
          return api(originalRequest)
        }
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError)
        
        // Clear all authentication data
        SessionSecurity.clearSession()
        
        // Redirect to login
        window.location.href = '/login'
        return Promise.reject(refreshError)
      }
    }

    // Handle network errors
    if (!error.response) {
      if (error.code === 'ECONNABORTED') {
        throw new Error('Request timeout. Please check your connection and try again.')
      }
      throw new Error('Network error. Please check your connection.')
    }

    return Promise.reject(error)
  }
)

// Generic API methods
export const apiClient = {
  get: (url, config = {}) => api.get(url, config),
  post: (url, data = {}, config = {}) => api.post(url, data, config),
  put: (url, data = {}, config = {}) => api.put(url, data, config),
  patch: (url, data = {}, config = {}) => api.patch(url, data, config),
  delete: (url, config = {}) => api.delete(url, config),
}

// Error handling helper
export const handleApiError = (error) => {
  if (error.response) {
    // Server responded with error status
    const message = error.response.data?.detail || error.response.data?.message || 'An error occurred'
    const apiError = new Error(message)
    apiError.status = error.response.status
    apiError.data = error.response.data
    apiError.message = message
    return apiError
  } else if (error.request) {
    // Request made but no response received
    const apiError = new Error('Network error. Please check your connection.')
    apiError.status = 0
    apiError.data = null
    return apiError
  } else {
    // Something else happened
    const apiError = new Error(error.message || 'An unexpected error occurred')
    apiError.status = 0
    apiError.data = null
    return apiError
  }
}

export default api